import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { companyEmailDomain } from '../../lib/emailDomain';
import { STATE_LABELS } from '../../lib/workspaces';
import { startViewAs } from '../../stores/authStore';
import type { Profile, Workspace } from '../../types/database';

const fullName = (p: Pick<Profile, 'first_name' | 'last_name'>) =>
  [p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)';

type Membership = {
  role: 'owner' | 'admin' | 'member';
  workspaces: Pick<Workspace, 'id' | 'name' | 'brand_url' | 'state'> | null;
};

// A User's profile, Customer and every Workspace they own or belong to, read with the
// Staff's own session through the Staff read policies.
export function StaffUser() {
  const { id } = useParams();
  const [viewAsError, setViewAsError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['staff-user', id],
    queryFn: async () => {
      const [profile, memberships, lastSignIn] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', id!).maybeSingle(),
        supabase
          .from('workspace_members')
          .select('role, workspaces:workspace_id(id, name, brand_url, state)')
          .eq('user_id', id!)
          .order('created_at'),
        supabase.rpc('staff_last_sign_in', { target: id! }),
      ]);
      if (profile.error) throw profile.error;
      if (lastSignIn.error) throw lastSignIn.error;
      if (memberships.error) throw memberships.error;

      // Others signed up with the same company email domain; free providers say nothing.
      const domain = companyEmailDomain(profile.data?.email ?? null);
      const colleagues = domain
        ? await supabase
            .from('profiles')
            .select('id, email, first_name, last_name')
            .ilike('email', `%@${domain}`)
            .neq('id', id!)
            .order('created_at')
            .limit(50)
        : null;
      if (colleagues?.error) throw colleagues.error;

      return {
        profile: profile.data as Profile | null,
        memberships: memberships.data as unknown as Membership[],
        lastSignIn: lastSignIn.data,
        domain,
        colleagues: colleagues?.data ?? [],
      };
    },
  });

  if (isLoading) return <div className="loading-spinner" />;
  if (error) return <p className="text-size-small staff-error">{error.message}</p>;
  if (!data?.profile) return <p className="text-size-small text-colour-alt-grey">No User with this id.</p>;

  const { profile: p, memberships, domain, colleagues, lastSignIn } = data;

  return (
    <div className="staff-detail">
      <div className="staff-header">
        <h1 className="text-size-large">{fullName(p)}</h1>
        <button
          type="button"
          className="button is-small w-button"
          onClick={() => startViewAs(p.id).catch((e: Error) => setViewAsError(e.message))}
        >
          View as
        </button>
      </div>
      {viewAsError && <p className="text-size-small staff-error">{viewAsError}</p>}
      <dl className="staff-fields">
        <dt>Email</dt>
        <dd>{p.email ?? 'None'}</dd>
        <dt>Customer</dt>
        <dd>
          {p.stripe_customer_id ? (
            <a href={`https://dashboard.stripe.com/customers/${p.stripe_customer_id}`} target="_blank" rel="noreferrer">
              {p.stripe_customer_id}
            </a>
          ) : (
            'None'
          )}
        </dd>
        <dt>Staff</dt>
        <dd>{p.is_admin ? 'Yes' : 'No'}</dd>
        <dt>Onboarding completed</dt>
        <dd>{p.onboarding_completed ? 'Yes' : 'No'}</dd>
        <dt>Trial used</dt>
        <dd>{p.trial_used_at ? new Date(p.trial_used_at).toLocaleString() : 'No'}</dd>
        <dt>Signed up</dt>
        <dd>{new Date(p.created_at).toLocaleString()}</dd>
        <dt>Last signed in</dt>
        <dd>{lastSignIn ? new Date(lastSignIn).toLocaleString() : 'Never'}</dd>
        <dt>Last active</dt>
        <dd>{p.last_active_at ? new Date(p.last_active_at).toLocaleString() : 'Never'}</dd>
      </dl>

      <div className="staff-results">
        <section>
          <h2 className="text-size-medium text-weight-semibold">Workspaces</h2>
          {memberships.length === 0 && <p className="text-size-small text-colour-alt-grey">No Workspaces.</p>}
          {memberships.map(
            ({ role, workspaces: w }) =>
              w && (
                <Link key={w.id} to={`/staff/workspaces/${w.id}`} className="staff-result">
                  <span>
                    {w.name} <span className="text-colour-alt-grey">{w.brand_url}</span>
                  </span>
                  <span className="text-colour-alt-grey">
                    {role} · {w.state ? STATE_LABELS[w.state] : 'Unknown'}
                    {w.id === p.workspace_id && ' · current'}
                  </span>
                </Link>
              )
          )}
        </section>
        {domain && (
          <section>
            <h2 className="text-size-medium text-weight-semibold">Other Users at {domain}</h2>
            {colleagues.length === 0 && <p className="text-size-small text-colour-alt-grey">None.</p>}
            {colleagues.map((u) => (
              <Link key={u.id} to={`/staff/users/${u.id}`} className="staff-result">
                <span>{fullName(u)}</span>
                <span className="text-colour-alt-grey">{u.email}</span>
              </Link>
            ))}
          </section>
        )}
      </div>
      <Link to={`/staff/activity?user=${p.id}`} className="small-link">Staff activity on this User</Link>
    </div>
  );
}
